import {
  Flex,
  Text,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import type { ScaffolderTaskStatus } from '@backstage/plugin-scaffolder-common';
import { TASK_STATUSES } from './hooks/use-task-explorer';
import { STATUS_COLORS, STATUS_HEX_COLORS } from './task-explorer-utils';

const DATE_RANGE_OPTIONS = [
  { label: 'All time', value: 'all' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 60 days', value: '60d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'Last 120 days', value: '120d' },
  { label: 'Last 180 days', value: '180d' },
];

const ALL_TEMPLATES_VALUE = '';

type TaskExplorerFiltersProps = {
  onCreatedByChange: (value: string) => void;
  onTemplateFilterChange: (value: string[]) => void;
  onStatusChange: (statuses: ScaffolderTaskStatus[]) => void;
  onDateRangeChange: (range: string) => void;
  selectedStatuses: ScaffolderTaskStatus[];
  createdBy: string;
  dateRange: string;
  templateFilter: string[];
  templateOptions: Array<{ label: string; value: string }>;
  userOptions: string[];
};

export const TaskExplorerFilters = ({
  onCreatedByChange,
  onTemplateFilterChange,
  onStatusChange,
  onDateRangeChange,
  selectedStatuses,
  createdBy,
  dateRange,
  templateFilter,
  templateOptions,
  userOptions,
}: TaskExplorerFiltersProps) => {
  const handleSelectionChange = (keys: Set<string | number>) => {
    onStatusChange([...keys] as ScaffolderTaskStatus[]);
  };

  const userSelectOptions = [
    { label: 'All users', value: '' },
    ...userOptions.map(userRef => ({
      label: userRef.split('/').pop() ?? userRef,
      value: userRef,
    })),
  ];

  const templateSelectOptions = [
    { label: 'All templates', value: ALL_TEMPLATES_VALUE },
    ...templateOptions,
  ];

  // An empty filter means every template, which the "All templates" option represents.
  const templateSelectValue =
    templateFilter.length > 0 ? templateFilter : [ALL_TEMPLATES_VALUE];

  const handleTemplateSelectionChange = (selected: string[]) => {
    const wasAllSelected = templateFilter.length === 0;
    const isAllSelected = selected.includes(ALL_TEMPLATES_VALUE);

    if (isAllSelected && !wasAllSelected) {
      onTemplateFilterChange([]);
      return;
    }
    onTemplateFilterChange(
      selected.filter(value => value !== ALL_TEMPLATES_VALUE),
    );
  };

  return (
    <Flex direction="column" gap="4">
      <Text variant="title-x-small">Filters</Text>

      <Flex direction="row" gap="4" align="end" style={{ flexWrap: 'wrap' }}>
        <Flex
          direction="column"
          gap="1"
          style={{ minWidth: 220, maxWidth: 320 }}
        >
          <Select
            label="User"
            selectionMode="single"
            searchable
            searchPlaceholder="Search users..."
            options={userSelectOptions}
            value={createdBy}
            onChange={selected => onCreatedByChange(selected as string)}
          />
        </Flex>

        <Flex
          direction="column"
          gap="1"
          style={{ minWidth: 280, maxWidth: 380 }}
        >
          <Select
            label="Template"
            selectionMode="multiple"
            searchable
            searchPlaceholder="Search templates..."
            options={templateSelectOptions}
            value={templateSelectValue}
            onChange={selected =>
              handleTemplateSelectionChange(
                [...(selected as Iterable<string | number>)].map(String),
              )
            }
          />
        </Flex>

        <Flex direction="column" gap="1" style={{ minWidth: 160 }}>
          <Select
            label="Date range"
            selectionMode="single"
            options={DATE_RANGE_OPTIONS}
            value={dateRange || 'all'}
            onChange={selected =>
              onDateRangeChange(selected === 'all' ? '' : (selected as string))
            }
          />
        </Flex>

        <Flex direction="column" gap="2">
          <Text variant="body-small">Status</Text>
          <ToggleButtonGroup
            selectionMode="multiple"
            selectedKeys={selectedStatuses}
            onSelectionChange={handleSelectionChange}
            aria-label="Filter by status"
          >
            {TASK_STATUSES.map(status => (
              <ToggleButton
                key={status}
                id={status}
                size="small"
                style={{ fontWeight: 'normal' }}
              >
                <span
                  style={{
                    color:
                      STATUS_HEX_COLORS[STATUS_COLORS[status] ?? 'secondary'],
                    marginRight: 4,
                  }}
                >
                  ●
                </span>
                {status}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Flex>
      </Flex>
    </Flex>
  );
};
